import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { LoadFailed } from "@/components/LoadState";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { timeAgo } from "@/lib/constants";
import { Handshake, Mail, Link2, CheckCircle2, XCircle, Loader2, Inbox, Lock } from "lucide-react";
import { toast } from "@/lib/toast";

const TABS = ["open", "in_progress", "resolved", "all"];
const TAB_LABEL = { open: "Open", in_progress: "In Review", resolved: "Resolved", all: "All" };
const STATUS_META = {
  open: { label: "Open", cls: "border-volt/50 text-volt" },
  in_progress: { label: "In Review", cls: "border-yellow-500/50 text-yellow-400" },
  resolved: { label: "Resolved", cls: "border-green-500/50 text-green-400" },
};
const DECISION_META = {
  approved: { label: "Approved", cls: "border-green-500/60 text-green-400 bg-green-500/10", icon: CheckCircle2 },
  disapproved: { label: "Disapproved", cls: "border-red-500/60 text-red-400 bg-red-500/10", icon: XCircle },
};

export default function AdminPartnerApplications() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});        // feedback_id -> internal note draft

  const load = (key) => {
    const qs = key === "all" ? "" : `?status=${key}`;
    setLoadErr(null);
    api.get(`/admin/partner-applications${qs}`)
      .then(({ data }) => setItems(data.applications || []))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load partner applications."));
  };

  useEffect(() => { if (user && isAdminRole(user.role)) load(tab); /* eslint-disable-next-line */ }, [tab, user]);

  const decide = async (id, decision) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/partner-applications/${id}/decision`, { decision, note: (notes[id] || "").trim() });
      if (decision === "approved") {
        toast.success(data?.created_draft_partner
          ? "Approved — draft partner created. Add a logo in Admin → Partners to publish it live."
          : "Application approved.");
      } else {
        toast.success("Application disapproved.");
      }
      load(tab);
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Couldn't record the decision.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return null;
  if (!user || !isAdminRole(user.role)) return <Navigate to="/" replace />;

  const DecisionForm = ({ it }) => {
    const busy = busyId === it.feedback_id;
    return (
      <div className="mt-3 border border-sniper bg-input/60 p-3" data-testid={`partner-app-decision-form-${it.feedback_id}`}>
        <label className="flex items-center gap-1.5 font-mono uppercase text-[10px] tracking-wide text-muted-foreground mb-1.5">
          <Lock className="w-3 h-3" /> Internal note — admin only, never shown to the applicant
        </label>
        <textarea
          value={notes[it.feedback_id] ?? ""}
          onChange={(e) => setNotes((n) => ({ ...n, [it.feedback_id]: e.target.value }))}
          rows={2}
          placeholder="Why this decision?"
          data-testid={`partner-app-note-${it.feedback_id}`}
          className="w-full bg-background border border-sniper px-3 py-2 text-sm text-foreground resize-y outline-none focus:border-olive"
        />
        <div className="flex flex-wrap gap-2 mt-2.5">
          <button onClick={() => decide(it.feedback_id, "approved")} disabled={busy}
            data-testid={`partner-app-approve-${it.feedback_id}`}
            className="inline-flex items-center gap-1.5 font-mono uppercase text-xs px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white font-bold transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Approve
          </button>
          <button onClick={() => decide(it.feedback_id, "disapproved")} disabled={busy}
            data-testid={`partner-app-disapprove-${it.feedback_id}`}
            className="inline-flex items-center gap-1.5 font-mono uppercase text-xs px-3 py-1.5 bg-red-700 hover:bg-red-600 text-white font-bold transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Disapprove
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl" data-testid="admin-partner-applications">
      <div className="flex items-center gap-2.5 mb-1">
        <Handshake className="w-6 h-6 text-olive-bright" />
        <h1 className="font-heading text-3xl font-bold uppercase text-foreground">Partner Applications</h1>
      </div>
      <p className="font-mono text-sm text-muted-foreground mb-6">
        Applications from the Partners page “Become a Partner” form. A copy is emailed to sales@opticswap.com as they arrive.
      </p>

      <div className="flex flex-wrap gap-1 mb-5 border-b border-sniper">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`partner-apps-tab-${t}`}
            className={`font-mono uppercase text-xs tracking-wide px-4 py-2.5 border-b-2 -mb-px transition-colors ${
              tab === t ? "border-olive-bright text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {loadErr ? (
        <LoadFailed message={loadErr} onRetry={() => load(tab)} />
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground" data-testid="partner-apps-empty">
          <Inbox className="w-10 h-10 mb-3 opacity-50" />
          <p className="font-mono uppercase text-xs tracking-wide">No {tab === "all" ? "" : TAB_LABEL[tab].toLowerCase()} partner applications</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const ci = it.collaborator_inquiry || {};
            const name = ci.name || "—";
            const email = ci.email || it.user_email || "";
            const link = ci.link || "";
            const decided = DECISION_META[it.decision];
            const isTerminal = !!decided || it.status === "resolved";
            const st = STATUS_META[it.status] || STATUS_META.open;
            const Badge = decided || null;
            return (
              <li key={it.feedback_id} data-testid={`partner-app-${it.feedback_id}`}
                className="border border-sniper bg-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-bold text-foreground truncate">{name}</div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 font-mono text-xs">
                      {email && (
                        <a href={`mailto:${email}`} className="inline-flex items-center gap-1 text-olive-bright hover:underline" data-testid={`partner-app-email-${it.feedback_id}`}>
                          <Mail className="w-3.5 h-3.5" /> {email}
                        </a>
                      )}
                      {link && (
                        <a href={link.startsWith("http") ? link : `https://${link}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-secondary-foreground hover:text-foreground" data-testid={`partner-app-link-${it.feedback_id}`}>
                          <Link2 className="w-3.5 h-3.5" /> {link}
                        </a>
                      )}
                      <span className="text-muted-foreground">{timeAgo(it.created_at)}</span>
                    </div>
                  </div>
                  {Badge ? (
                    <span className={`shrink-0 inline-flex items-center gap-1 font-mono uppercase text-[10px] tracking-wide px-2 py-1 border ${Badge.cls}`}
                      data-testid={`partner-app-decision-badge-${it.feedback_id}`}>
                      <Badge.icon className="w-3 h-3" /> {Badge.label}
                    </span>
                  ) : (
                    <span className={`shrink-0 font-mono uppercase text-[10px] tracking-wide px-2 py-1 border ${st.cls}`}>{st.label}</span>
                  )}
                </div>

                {ci.message || it.message ? (
                  <div className="mt-3 p-3 bg-input border border-sniper text-secondary-foreground text-sm whitespace-pre-wrap">
                    {ci.message || it.message}
                  </div>
                ) : null}

                {/* Decision record (who decided, when, and the internal note) */}
                {decided && (
                  <div className="mt-3 border-l-2 border-olive bg-obsidian/40 pl-3 py-2" data-testid={`partner-app-decision-record-${it.feedback_id}`}>
                    <div className="font-mono text-[11px] text-muted-foreground">
                      <span className={it.decision === "approved" ? "text-green-400" : "text-red-400"}>{decided.label}</span>
                      {it.decided_by_username ? <> by <span className="text-foreground">{it.decided_by_username}</span></> : null}
                      {it.decided_at ? <> · {timeAgo(it.decided_at)}</> : null}
                    </div>
                    {it.decision_note ? (
                      <div className="mt-1.5 flex items-start gap-1.5 text-sm text-secondary-foreground" data-testid={`partner-app-note-display-${it.feedback_id}`}>
                        <Lock className="w-3 h-3 mt-1 shrink-0 text-muted-foreground" />
                        <span className="whitespace-pre-wrap">{it.decision_note}</span>
                      </div>
                    ) : (
                      <div className="mt-1 text-xs text-muted-foreground italic">No internal note.</div>
                    )}
                    {it.decision === "approved" && (
                      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-wide text-olive-bright">
                        Published to Partners · add a logo in Admin → Partners to go live
                      </div>
                    )}
                  </div>
                )}

                {/* Legacy terminal state: resolved without a recorded approve/disapprove decision */}
                {!decided && isTerminal && (
                  <div className="mt-3 border-l-2 border-sniper bg-obsidian/40 pl-3 py-2 font-mono text-[11px] text-muted-foreground" data-testid={`partner-app-resolved-nodecision-${it.feedback_id}`}>
                    Resolved — no approve/disapprove decision was recorded.
                  </div>
                )}

                {/* Actions: only PENDING (non-terminal) applications can be acted on. A decision is
                    terminal — decided or resolved cards show the badge + note only, no buttons. */}
                {!isTerminal && <DecisionForm it={it} />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
